import {
  cloneDeep, debounce, isNil, isObject, omitBy,
} from 'lodash';
import loadable from '@/mixins/loadable';
import axios from 'axios';
import api from '@/modules/searcher/api';

/**
 * @typedef {{}} SearcherResult
 * @property {number} entityId - The entity identifier
 * @property {string} entityType - The entity type (content, taxonomy....)
 * @property {Object} entity - The entity card representation (ContentCard, TaxonomyCard etc...)
 */

/**
 * Searchable mixin
 * This mixin gives a component the ability to search entities
 *
 * The component MUST implement the following props, data or computed
 * - entity (string)
 * - filters (object)
 * - sort (object) optional
 * - text (string) optional
 */
export default {
  mixins: [loadable],
  data() {
    return {
      items: [],
      loading: false,
      numPages: 0,
      numResults: 0,
      page: 1,
      searchOnFiltersChange: true,
      searchText: this.text || null,
    };
  },
  created() {
    if (!this.entity) throw new Error('searchable mixin needs an entity!');
    if (typeof this.filters !== 'object') throw new Error('searchable mixin needs a filters object!');
  },
  watch: {
    // Set page to 1 if filters changes
    filters() {
      this.resetPage();
    },
    // Set page to 1 if searchText changes
    searchText() {
      this.resetPage();
    },
    // Set page to 1 if sort changes
    sort() {
      this.resetPage();
    },
    // Watch computed filters for search launch
    searchFilters: {
      async handler(value) {
        if (!this.searchOnFiltersChange && !value.filters.text) {
          return;
        }
        // TODO: implement 'all' search
        if (value.entity === 'all') {
          return;
        }

        this.search(value);
      },
      immediate: true,
    },
  },
  methods: {
    resetPage() {
      this.items = [];
      this.page = 1;
    },
    async refresh() {
      this.resetPage();
      return this.search(this.searchFilters);
    },
    async refreshItem(item) {
      console.debug('refresh item', item, this.searchFilters);
      this.loadingStart();
      const filters = { text: item.id.toString() };
      const response = await api.search(this.searchFilters.entity, filters, this.searchTokenSource());
      this.loadingFinish();
      if (response.results.length !== 1) {
        // Something went wrong, force refresh
        console.warn('Cannot find item by id to refresh it');
        await this.refresh();
        return;
      }

      // Search current item and replace it
      const freshItem = response.results.filter(result => result.id === item.id)
        .pop();
      if (!freshItem) {
        // Something went wrong, force refresh
        console.warn('Cannot find item by id in results to refresh it');
        await this.refresh();
        return;
      }

      const index = this.items.findIndex(currentItem => currentItem.entityId === item.id);
      if (index === -1) {
        console.warn('Cannot find item by id in current items');
        await this.refresh();
        return;
      }

      this.$set(this.items, index, this.formatEntity(freshItem));
    },
    /**
     * Hook lanzado antes de buscar. Por defecto no hace nada, pero permite configurar el polling automático de los
     * buscadores principales del CMS.
     * @param {{entity: string, filters: {}}} config
     */
    beforeSearch(config) {
    },
    /**
     * Realiza la búsqueda y actualiza los resultados.
     * @param {{entity: string, filters: {}}} config
     */
    async search(config) {
      this.beforeSearch(config);

      this.loadingStart();
      const response = await api.search(config.entity, config.filters, this.searchTokenSource());
      this.loadingFinish();

      if (response) {
        this.items = this.items.concat(response.results.filter(result => isObject(result))
          .map(this.formatEntity));
        this.page = response.page || this.page;
        this.numResults = response.numResults || 0;
        this.numPages = response.numPages || 0;
      }

      this.$emit('search:results', {
        items: this.items,
        page: this.page,
        numResults: this.numResults,
        numPages: this.numPages,
      });

      this.afterSearch(config, response);
    },
    /**
     * Hook lanzado después de buscar. Por defecto no hace nada, pero permite configurar el polling automático de los
     * buscadores principales del CMS.
     * @param {{entity: string, filters: {}}} config
     * @param {SearchResponse} response
     */
    afterSearch(config, response) {
    },
    // eslint-disable-next-line
    searchByText: debounce(function (event) {
      const searchText = isObject(event) ? event.target.value : event;
      if (this.searchText === searchText) return;
      this.searchText = searchText;

      const newFilters = cloneDeep(this.filters);
      if (this.searchText) {
        newFilters.text = this.searchText;
      } else if (this.filters.text) {
        delete newFilters.text;
      }
      this.setFilters(newFilters);
    }, 700),
    loadMore() {
      this.page += 1;
    },
    /**
     * @param {any} filters
     * @param {boolean} silent - don't push a new router state, useful on default initial filter updates
     */
    setFilters(filters, silent = false) {
      /** @event Searchable#setFilters */
      this.$emit('setFilters', filters, silent);
    },
    setSearchLimit(limit) {
      this.setFilters({
        ...this.filters,
        limit,
      });
    },
    updateFilters(filters, silent = false) {
      const newFilters = this.text ? { text: this.text, ...filters } : filters;
      this.setFilters(newFilters, silent);
    },
    mergeFilters(filters) {
      const newFilters = this.text ? { text: this.text, ...this.filters, ...filters } : { ...this.filters, ...filters };
      this.setFilters(newFilters);
    },
    resetFilters() {
      this.setFilters(this.filters);
    },
    updateSort(sort) {
      console.log(sort);
      /** @event Searchable#updateSort */
      this.$emit('updateSort', sort);
    },
    /*
     * @return {SearcherResult}
     */
    formatEntity(result) {
      return {
        entityId: result.id,
        entityType: this.entity,
        entity: result,
      };
    },
  },
  computed: {
    searchLimitConfig() {
      return this.$store.getters.searchLimitConfigForEntity(this.entity);
    },
    searchLimitOptions() {
      return this.searchLimitConfig.available;
    },
    searchLimit() {
      return parseInt(this.filters.limit || this.searchLimitConfig.default, 10);
    },
    searchFilters() {
      const searchFilters = {
        entity: this.entity,
        filters: {
          ...omitBy(this.filters, isNil),
          page: this.page,
        },
      };
      if (this.sort) {
        searchFilters.filters.sort = this.sort;
      }
      return searchFilters;
    },
    hasMorePages() {
      return this.page < this.numPages;
    },
    /**
     * Returns a new CancelToken from axios.
     * If there already is an active token, it will be canceled before returning the new one.
     *
     * @return {function(): CancelTokenSource}
     */
    searchTokenSource() {
      let token = null;
      return () => {
        if (token && token.cancel) {
          token.cancel();
        }
        token = axios.CancelToken.source();
        return token;
      };
    },
  },
};
